<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed, reactive, ref } from 'vue'
import { VueRecaptcha } from 'vue-recaptcha'
import SocialLogin from './social-login'
import consentService from '@services/consent-service'
import { useApi } from '@composables/use-api'
import postAuthRedirect from '@utils/post-auth-redirect'
import { email as emailValidator, required, sameAs, useFormValidation } from '@composables/use-form-validation'
import { useAppearanceStore } from '@stores/appearance'
import { useAuthStore } from '@stores/auth'
import { publicConfig } from '@/config/runtime'
import { useMessages } from '@composables/use-messages'

defineProps({
    redirect: {
        type: [String, Boolean],
        default: false,
    },
})

const getMessageText = useMessages()
const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const form = reactive({ email: '', password: '' })
const confirmEmail = ref('')
const confirmPassword = ref('')
const tryingToSignUp = ref(false)
const recaptchaKey = ref('')
const siteKey = computed(() => publicConfig().recaptchaSiteKey || '')
const validation = useFormValidation({
    email: {
        value: computed(() => form.email),
        validators: [required, emailValidator],
    },
    confirmEmail: {
        value: confirmEmail,
        validators: [required, emailValidator, sameAs(() => form.email)],
    },
    password: { value: computed(() => form.password), validators: [required] },
    confirmPassword: {
        value: confirmPassword,
        validators: [required, sameAs(() => form.password)],
    },
})

function notify(messageKey, type = 'warning', params = {}) {
    appearanceStore.handleNotification({
        message: getMessageText(messageKey, params),
        type,
    })
}

function setRecaptcha(value) {
    recaptchaKey.value = value || ''
}

function handleSuccess(response) {
    const token = response.headers['x-auth-token']
    if (token) {
        const user = { ...response.body, token }
        authStore.setUser({ ...user, members_id: { id: user.id } })
    }
    consentService.grantConsent('signup')
    postAuthRedirect.resolveAfterAuth('/premium')
}

function handleError(error) {
    tryingToSignUp.value = false
    if (error.response.status === 409) {
        const networkUsed = error.response.headers['x-network-used']
        const messageKey = networkUsed === 'none'
            ? 'USER_ALREADY_EXISTS'
            : 'USER_ALREADY_EXISTS_SOCIAL'
        const params = networkUsed === 'none' ? {} : { '{social}': networkUsed }
        notify(messageKey, 'warning', params)
        return
    }
    notify('SOMETHING_WENT_WRONG', 'danger')
}

function signUp() {
    if (validation.$invalid) {
        notify('EMAIL_PASSWORD_REQUIRED')
        return undefined
    }
    if (!recaptchaKey.value) {
        notify('RECAPTCHA_MISSING_ERROR')
        return undefined
    }

    tryingToSignUp.value = true
    const request = { ...form, username: form.email }
    return useApi().members.post({}, request).then(handleSuccess).catch(handleError)
}

defineOptions({ name: 'SignUp' })

usePageHead({ title: 'Sign Up', description: 'Sign Up' })
</script>

<template>
    <BaseContent>
        <BaseSection padded>
            <div class="signup-form-container">
                <h4 class="signup-title">Sign up</h4>
                <SocialLogin :is-sign-up="true" />
                <div class="signup-line-separator">OR</div>
                <h5 class="signup-subtitle">Sign up with your email address:</h5>
                <form
                    class="signup-form"
                    autocomplete="off | unknown-autocomplete-value"
                    @submit.prevent="signUp"
                >
                    <div class="signup-field-container">
                        <BaseInput
                            v-model="form.email"
                            :class="['signup-field', validation.email.$error && 'signup-error']"
                            placeholder="email*"
                            @blur="validation.email.$touch()"
                        />
                        <label
                            v-if="validation.email.$error"
                            :class="['signup-label', 'signup-error']"
                        >This field is required and must be an email</label>
                    </div>
                    <div class="signup-field-container">
                        <BaseInput
                            v-model="confirmEmail"
                            :class="['signup-field', validation.confirmEmail.$error && 'signup-error']"
                            placeholder="confirm email*"
                            @blur="validation.confirmEmail.$touch()"
                        />
                        <label
                            v-if="validation.confirmEmail.$error"
                            :class="['signup-label', 'signup-error']"
                        >This field is required and must match the email above</label>
                    </div>
                    <div class="signup-field-container">
                        <BaseInput
                            v-model="form.password"
                            :class="['signup-field', validation.password.$error && 'signup-error']"
                            type="password"
                            placeholder="password*"
                            @blur="validation.password.$touch()"
                        />
                        <label
                            v-if="validation.password.$error"
                            :class="['signup-label', 'signup-error']"
                        >This field is required</label>
                    </div>
                    <div class="signup-field-container">
                        <BaseInput
                            v-model="confirmPassword"
                            :class="['signup-field', validation.confirmPassword.$error && 'signup-error']"
                            type="password"
                            placeholder="confirm password*"
                            @blur="validation.confirmPassword.$touch()"
                        />
                        <label
                            v-if="validation.confirmPassword.$error"
                            :class="['signup-label', 'signup-error']"
                        >This field is required and must match password above</label>
                    </div>
                    <div class="signup-recaptcha-container">
                        <VueRecaptcha
                            :load-recaptcha-script="true"
                            :sitekey="siteKey"
                            theme="dark"
                            @verify="setRecaptcha"
                            @expired="setRecaptcha"
                        />
                    </div>
                    <div class="signup-agreement">
                        By signing up you agree to our
                        <a
                            class="signup-link"
                            href="https://frisky.fm/about/terms"
                            target="_blank"
                        >Terms and Conditions</a>
                        &
                        <a
                            class="signup-link"
                            href="https://frisky.fm/about/privacy-policy"
                            target="_blank"
                        >Privacy Policy</a>.
                    </div>
                    <BaseButton
                        class="signup-button"
                        :disabled="tryingToSignUp"
                        variant="primary"
                        fill
                        type="submit"
                    >
                        Sign Up
                    </BaseButton>
                    <div class="signup-additional-options">
                        <span class="signup-additional-text">Already have an account?</span>
                        <BaseLink :to="{ name: 'login' }">
                            <BaseButton
                                variant="secondary"
                                size="m"
                            >Log In</BaseButton>
                        </BaseLink>
                    </div>
                </form>
            </div>
        </BaseSection>
    </BaseContent>
</template>

<style lang="scss" scoped>
@import '@design';

.signup-form-container {
    position: relative;
    margin-bottom: $s-h;
    .signup-field {
        &.signup-error {
            margin-bottom: $s-xl;
            border-color: $c-status-error-text;
            border-width: 2px;
        }
    }
}
.signup-field-container {
    position: relative;
    width: 100%;
}
.signup-form {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.signup-label {
    @extend %contentSubtitleM;

    display: block;
    font-weight: 300;
    text-align: left;

    &.signup-error {
        position: absolute;
        bottom: $s-xs;
        color: $c-status-error-text;
    }
}
.signup-title {
    text-align: left;
}
.signup-subtitle {
    @extend %contentTinyHeading;

    font-weight: 400;
    text-align: left;
}

.signup-line-separator {
    @include lineSeparator(true, $c-border-default);

    @extend %contentSubtitleS;

    font-weight: 100;
}

.signup-recaptcha-container {
    margin-top: $s-l;
}

.signup-button {
    margin-top: $s-xl;
}

.signup-agreement {
    @extend %contentSubtitleS;

    margin-top: $s-l;
}

a.signup-link,
a.signup-link:visited {
    color: $c-status-error-text;
}

.signup-additional {
    // The Log In pill hugs its label now, so it shares a row with the prompt at every
    // width; the gap is what keeps them apart (template whitespace between the two is
    // condensed away). Wraps onto two centred lines if a phone is too narrow for both.
    &-options {
        display: flex;
        flex-wrap: wrap;
        row-gap: $space-12;
        column-gap: $space-24;
        align-items: center;
        justify-content: center;
        margin-top: $s-h;
    }
    &-text {
        @extend %contentSubtitleM;
    }
}
</style>
